import {
  validateCanonicalTreeExternalEvidenceSummary,
} from './CanonicalTreeExternalEvidenceReadOnlyAdapterContract';

import type {
  CanonicalTreeExternalEvidenceObservabilitySummary,
} from './CanonicalTreeExternalEvidenceObservability';

import type {
  CanonicalTreeEvidenceAvailability,
  CanonicalTreeEvidencePublisherLifecycle,
} from './CanonicalTreeEvidencePublicationOwnershipAuthority';

export type CanonicalTreeTabScopedEvidenceRegistryListener =
  () => void;

export type CanonicalTreeTabScopedEvidenceRegistryUnsubscribe =
  () => void;

export interface CanonicalTreeTabScopedEvidenceRegistrySnapshot {
  readonly milestone:
    'CC-TREE-ARCH-038';

  readonly registry:
    'TAB_SCOPED_SANITIZED_EVIDENCE_REGISTRY';

  readonly scope:
    'TAB_MEMORY_ONLY';

  readonly publisherLifecycle:
    CanonicalTreeEvidencePublisherLifecycle;

  readonly evidenceAvailability:
    CanonicalTreeEvidenceAvailability;

  readonly authenticatedOwnerPresent:
    boolean;

  readonly summary:
    CanonicalTreeExternalEvidenceObservabilitySummary | null;

  readonly lastKnownSnapshotReadable:
    boolean;

  readonly revision:
    number;

  readonly changedAt:
    string | null;
}

export interface CanonicalTreeTabScopedEvidenceRegistry {
  readonly getSnapshot:
    () => CanonicalTreeTabScopedEvidenceRegistrySnapshot;

  readonly subscribe: (
    listener:
      CanonicalTreeTabScopedEvidenceRegistryListener,
  ) => CanonicalTreeTabScopedEvidenceRegistryUnsubscribe;

  readonly publishActive: (
    input: {
      readonly authenticatedOwnerPresent:
        boolean;

      readonly candidate:
        unknown;
    },
  ) => CanonicalTreeTabScopedEvidenceRegistrySnapshot;

  readonly markPublisherUnmounted: (
    input: {
      readonly authenticatedOwnerPresent:
        boolean;
    },
  ) => CanonicalTreeTabScopedEvidenceRegistrySnapshot;
}

const createAbsentSnapshot =
  (): CanonicalTreeTabScopedEvidenceRegistrySnapshot =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-038',

      registry:
        'TAB_SCOPED_SANITIZED_EVIDENCE_REGISTRY',

      scope:
        'TAB_MEMORY_ONLY',

      publisherLifecycle:
        'PUBLISHER_ABSENT',

      evidenceAvailability:
        'NO_AUTHENTICATED_OWNER',

      authenticatedOwnerPresent:
        false,

      summary:
        null,

      lastKnownSnapshotReadable:
        false,

      revision:
        0,

      changedAt:
        null,
    });

const summariesEqual = (
  left:
    CanonicalTreeExternalEvidenceObservabilitySummary | null,

  right:
    CanonicalTreeExternalEvidenceObservabilitySummary | null,
): boolean => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.action ===
      right.action &&
    left.error ===
      right.error &&
    left.lastAttemptStatus ===
      right.lastAttemptStatus &&
    left.memberCount ===
      right.memberCount &&
    left.ownerUserId ===
      right.ownerUserId &&
    left.readyForGateway ===
      right.readyForGateway &&
    left.refreshReason ===
      right.refreshReason &&
    left.status ===
      right.status
  );
};

const snapshotsSemanticallyEqual = (
  left:
    CanonicalTreeTabScopedEvidenceRegistrySnapshot,

  right: {
    readonly publisherLifecycle:
      CanonicalTreeEvidencePublisherLifecycle;

    readonly evidenceAvailability:
      CanonicalTreeEvidenceAvailability;

    readonly authenticatedOwnerPresent:
      boolean;

    readonly summary:
      CanonicalTreeExternalEvidenceObservabilitySummary | null;

    readonly lastKnownSnapshotReadable:
      boolean;
  },
): boolean =>
  left.publisherLifecycle ===
    right.publisherLifecycle &&
  left.evidenceAvailability ===
    right.evidenceAvailability &&
  left.authenticatedOwnerPresent ===
    right.authenticatedOwnerPresent &&
  left.lastKnownSnapshotReadable ===
    right.lastKnownSnapshotReadable &&
  summariesEqual(
    left.summary,
    right.summary,
  );

const resolveAvailability = (
  authenticatedOwnerPresent:
    boolean,

  summary:
    CanonicalTreeExternalEvidenceObservabilitySummary | null,
): CanonicalTreeEvidenceAvailability => {
  if (!authenticatedOwnerPresent) {
    return 'NO_AUTHENTICATED_OWNER';
  }

  if (
    summary?.status ===
    'LOADED'
  ) {
    return 'EVIDENCE_AVAILABLE';
  }

  if (
    summary?.status ===
    'STALE'
  ) {
    return 'EVIDENCE_STALE';
  }

  return 'EVIDENCE_UNAVAILABLE';
};

const mayRetainUnmountedSummary = (
  authenticatedOwnerPresent:
    boolean,

  summary:
    CanonicalTreeExternalEvidenceObservabilitySummary | null,
): boolean =>
  authenticatedOwnerPresent &&
  (
    summary?.status ===
      'LOADED' ||
    summary?.status ===
      'STALE'
  );

export const createCanonicalTreeTabScopedSanitizedEvidenceRegistry =
  (
    now:
      () => string =
      () => new Date().toISOString(),
  ): CanonicalTreeTabScopedEvidenceRegistry => {
    let currentSnapshot =
      createAbsentSnapshot();

    const listeners =
      new Set<
        CanonicalTreeTabScopedEvidenceRegistryListener
      >();

    const notifyListeners = (): void => {
      for (const listener of [
        ...listeners,
      ]) {
        try {
          listener();
        } catch {
          // A failed diagnostic consumer cannot interrupt publication.
        }
      }
    };

    const commit = (
      next: {
        readonly publisherLifecycle:
          CanonicalTreeEvidencePublisherLifecycle;

        readonly evidenceAvailability:
          CanonicalTreeEvidenceAvailability;

        readonly authenticatedOwnerPresent:
          boolean;

        readonly summary:
          CanonicalTreeExternalEvidenceObservabilitySummary | null;

        readonly lastKnownSnapshotReadable:
          boolean;
      },
    ): CanonicalTreeTabScopedEvidenceRegistrySnapshot => {
      if (
        snapshotsSemanticallyEqual(
          currentSnapshot,
          next,
        )
      ) {
        return currentSnapshot;
      }

      currentSnapshot =
        Object.freeze({
          milestone:
            'CC-TREE-ARCH-038',

          registry:
            'TAB_SCOPED_SANITIZED_EVIDENCE_REGISTRY',

          scope:
            'TAB_MEMORY_ONLY',

          publisherLifecycle:
            next.publisherLifecycle,

          evidenceAvailability:
            next.evidenceAvailability,

          authenticatedOwnerPresent:
            next.authenticatedOwnerPresent,

          summary:
            next.summary,

          lastKnownSnapshotReadable:
            next.lastKnownSnapshotReadable,

          revision:
            currentSnapshot.revision +
            1,

          changedAt:
            now(),
        });

      notifyListeners();

      return currentSnapshot;
    };

    return Object.freeze({
      getSnapshot:
        () => currentSnapshot,

      subscribe: (
        listener:
          CanonicalTreeTabScopedEvidenceRegistryListener,
      ): CanonicalTreeTabScopedEvidenceRegistryUnsubscribe => {
        listeners.add(listener);

        let active = true;

        return () => {
          if (!active) {
            return;
          }

          active = false;
          listeners.delete(listener);
        };
      },

      publishActive: (
        input,
      ) => {
        if (
          input.authenticatedOwnerPresent ===
          false
        ) {
          return commit({
            publisherLifecycle:
              'PUBLISHER_ACTIVE',

            evidenceAvailability:
              'NO_AUTHENTICATED_OWNER',

            authenticatedOwnerPresent:
              false,

            summary:
              null,

            lastKnownSnapshotReadable:
              false,
          });
        }

        const summary =
          validateCanonicalTreeExternalEvidenceSummary(
            input.candidate,
          );

        return commit({
          publisherLifecycle:
            'PUBLISHER_ACTIVE',

          evidenceAvailability:
            resolveAvailability(
              true,
              summary,
            ),

          authenticatedOwnerPresent:
            true,

          summary,

          lastKnownSnapshotReadable:
            false,
        });
      },

      markPublisherUnmounted: (
        input,
      ) => {
        const retainSummary =
          mayRetainUnmountedSummary(
            input.authenticatedOwnerPresent,
            currentSnapshot.summary,
          );

        const summary =
          retainSummary
            ? currentSnapshot.summary
            : null;

        return commit({
          publisherLifecycle:
            'PUBLISHER_UNMOUNTED',

          evidenceAvailability:
            resolveAvailability(
              input.authenticatedOwnerPresent,
              summary,
            ),

          authenticatedOwnerPresent:
            input.authenticatedOwnerPresent,

          summary,

          lastKnownSnapshotReadable:
            retainSummary,
        });
      },
    });
  };

let defaultRegistry:
  CanonicalTreeTabScopedEvidenceRegistry | null =
  null;

export const getCanonicalTreeTabScopedSanitizedEvidenceRegistry =
  (): CanonicalTreeTabScopedEvidenceRegistry => {
    if (!defaultRegistry) {
      defaultRegistry =
        createCanonicalTreeTabScopedSanitizedEvidenceRegistry();
    }

    return defaultRegistry;
  };
